import { isNil } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { type Box3 } from 'three';
import {
  useCrossSection3d,
  useCrossSectionAxes,
  useCrossSectionInteractions,
  type InteractionState,
} from '../hooks';
import { fitCameraToData } from '../utils/';
import type { CrossSectionProfileProps } from './types';

export const CrossSectionProfile: React.FC<CrossSectionProfileProps> = ({
  data,
  highlightConfig,
  isSlopeToolActive = false,
  slopeToolState,
  onSlopePointClick,
  onHoverPoint,
  onPointClick,
  containerRef: externalContainerRef,
  pointCloudProperties,
}) => {
  // States.
  const [isInitialized, setIsInitialized] = useState(false);

  // Refs.
  const internalContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = externalContainerRef || internalContainerRef;
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const hasInitializedRef = useRef(false);
  const svgRef = useRef<SVGSVGElement>(null);

  // Interaction state.
  const interactionStateRef = useRef<InteractionState>({
    mouse: new THREE.Vector2(),
    isMouseDown: false,
    scale: new THREE.Vector3(1, 1, 1),
    projectedBounds: new THREE.Box3(),
  });

  const onInitComplete = useCallback(
    (bounds: Box3) => {
      interactionStateRef.current.projectedBounds = bounds;
      setIsInitialized(true);
    },
    [interactionStateRef],
  );

  // Initialize Three.js.
  const {
    threeJsRefs,
    renderScene,
    updateHighlightSprite,
    hideHighlightSprite,
  } = useCrossSection3d({
    data,
    canvasContainerRef,
    onInitComplete,
    pointCloudLayerProperties: pointCloudProperties,
  });

  // Initialize D3 axes.
  const { d3Refs, initializeSVG, updateSVGAxes, updateCameraBounds } =
    useCrossSectionAxes({
      svgRef,
      containerRef,
      threeJsRefs,
      interactionStateRef,
    });

  // Update scales and axes.
  const updateScalesAndAxes = useCallback(() => {
    updateCameraBounds();
    updateSVGAxes();
  }, [updateCameraBounds, updateSVGAxes, d3Refs]);

  // Handle initialization in a separate effect.
  // Only depend on isInitialized to prevent re-running when callbacks change
  useEffect(() => {
    if (
      !isInitialized ||
      hasInitializedRef.current ||
      isNil(threeJsRefs.current?.camera) ||
      isNil(canvasContainerRef.current)
    ) {
      return;
    }

    const { camera } = threeJsRefs.current;
    const { scale, projectedBounds } = interactionStateRef.current;

    if (isNil(scale) || isNil(projectedBounds)) return;

    const canvasWidth = canvasContainerRef.current.clientWidth;
    const canvasHeight = canvasContainerRef.current.clientHeight;

    fitCameraToData(camera, projectedBounds, scale, canvasWidth, canvasHeight);
    updateCameraBounds();
    initializeSVG();
    updateSVGAxes();
    renderScene();

    hasInitializedRef.current = true;
  }, [isInitialized]);

  // Initialize interactions.
  const { updateSlopePoints } = useCrossSectionInteractions({
    canvasContainerRef,
    data,
    d3Refs,
    threeJsRefs,
    interactionStateRef,
    highlightConfig,
    isSlopeToolActive,
    slopeToolState,
    onSlopePointClick,
    onHoverPoint,
    onPointClick,
    updateHighlightSprite,
    hideHighlightSprite,
    renderScene,
    updateScalesAndAxes,
  });

  // Resize handler.
  useEffect(() => {
    if (isNil(containerRef.current)) return;

    const resizeObserver = new ResizeObserver(() => {
      updateScalesAndAxes();
      if (isSlopeToolActive) {
        updateSlopePoints();
      }
      renderScene();
    });

    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, [updateScalesAndAxes, renderScene, isSlopeToolActive, updateSlopePoints]);

  // Render.
  if (isNil(data) || data.count === 0) {
    return null;
  }

  return (
    <div
      ref={containerRef}
      className="cross-section-profile"
      style={{ width: '100%', height: '100%' }}
    >
      <div className="cross-section-profile__content">
        <div
          ref={canvasContainerRef}
          className="cross-section-profile__canvas-container"
        />
        <svg ref={svgRef} className="cross-section-profile__axes" />
      </div>
    </div>
  );
};
